import type { Card } from "../types/cards-api.types";

// Filename says "Mocks" for legacy import-path reasons; values are real.
// `balanceMock` is `Card.balance.USD.current` from the DB cache.
export type CardAugmented = Omit<Card, "pocket"> & {
  balanceMock: number;
  monthSpend: number;
  pocketRef: { id: string; name: string; color: string; icon?: string | null } | null;
  exp: string;
  frozen: boolean;
  disabled: boolean;
};

const formatExp = (raw: Card): string => {
  if (raw.expiration_date) {
    const d = new Date(raw.expiration_date);
    if (!Number.isNaN(d.getTime())) {
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = String(d.getFullYear()).slice(-2);
      return `${mm}/${yy}`;
    }
  }
  if (raw.expiry_month && raw.expiry_year) {
    const mm = raw.expiry_month.padStart(2, "0");
    const yy = raw.expiry_year.slice(-2);
    return `${mm}/${yy}`;
  }
  return "**/**";
};

export function augmentCardWithMocks(card: Card, _index: number): CardAugmented {
  void _index;

  // JSONB stores strings — parse defensively.
  const rawCurrent = card.balance?.USD?.current as unknown;
  const parsedCurrent =
    typeof rawCurrent === "number"
      ? rawCurrent
      : typeof rawCurrent === "string"
        ? parseFloat(rawCurrent)
        : NaN;
  const balanceMock = Number.isFinite(parsedCurrent) ? parsedCurrent : 0;

  const monthSpend = typeof card.month_spend_usd === "number" ? card.month_spend_usd : 0;

  const status = (card.status || card.card_status || "").toUpperCase();

  const pocketRef: CardAugmented["pocketRef"] = card.pocket
    ? {
        id: card.pocket.id,
        name: card.pocket.name || `Pocket ${card.pocket.id.slice(0, 6)}`,
        color: card.pocket.color || "#0d8848",
        icon: card.pocket.icon ?? null,
      }
    : null;

  const { pocket: _pocket, ...rest } = card;
  void _pocket;

  return {
    ...rest,
    balanceMock,
    monthSpend,
    pocketRef,
    exp: formatExp(card),
    frozen: status === "FROZEN",
    disabled: status === "DISABLED",
  };
}
